import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

import Popup from "../../popup";
import useSources from "../useSources";
import useLayers from "../useLayers";
import useSearchRadius from "../useSearchRadius";
import { coordinatesGeocoder, MapLogger } from "./mapUtils";
import { BASEMAP_STYLES } from "../../constants";
import debounce from "lodash.debounce";
import createTheme from "../../../../theme";
import { ThemeProvider } from "styled-components/macro";
import { useSelector } from "react-redux";
import {
  jssPreset,
  StylesProvider,
  ThemeProvider as MuiThemeProvider,
} from "@material-ui/core/styles";
import { create } from "jss";
import DragCircleControl from "../../../../components/map/DragCircleControl";
import { RulerControl } from "mapbox-gl-controls";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import * as MapboxDrawGeodesic from "mapbox-gl-draw-geodesic";
import { handleCopyCoords, updateArea } from "../../../../utils/map";
import ResetZoomControl from "../../../../components/map/ResetZoomControl";
import { isTouchScreenDevice } from "../../../../utils";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import { useApp } from "../../../../AppProvider";

// Client-side validation limits (must match backend)
const REPORT_LIMITS = {
  radius: {
    soft: { miles: 3, feet: 15840, meters: 4828, acres: 18093 },
    hard: { miles: 5, feet: 26400, meters: 8047, acres: 50266 },
  },
};

const mapLogger = new MapLogger({
  enabled: process.env.NODE_ENV === "development",
  prefix: "Public Map",
});

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById("jss-insertion-point"),
});

/**
 * The `useMap` hook controls all of the Mapbox functionality. It controls
 * everything from rendering the map and popups to filtering layers to styling
 * layers.
 * The hook exposes the map instance in addition to a variety of handlers
 * responsible for applying filters and styles to the map
 * @param {object} ref a React ref for the map container
 * @param {*} mapConfig initial configuration options for the map
 * see https://docs.mapbox.com/mapbox-gl-js/api/map/
 */
const useMap = (ref, mapConfig) => {
  const { currentUser } = useApp();
  const { getAccessTokenSilently } = useAuth0();
  const theme = useSelector((state) => state.themeReducer);
  const [map, setMap] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [activeBasemap, setActiveBasemap] = useState(BASEMAP_STYLES[0].style);
  const [dataAdded, setDataAdded] = useState(false);
  const [dataVizVisible, setDataVizVisible] = useState(false);
  const [dataVizWellNumber, setDataVizWellNumber] = useState(null);
  const [dataVizGraphType, setDataVizGraphType] = useState(null);
  const [measurementsVisible, setMeasurementsVisible] = useState(false);
  const [virtualBoreCoordinates, setVirtualBoreCoordinates] = useState(null);
  const [virtualBoreVisible, setVirtualBoreVisible] = useState(false);
  const [wellsReportDialogOpen, setWellsReportDialogOpen] = useState(false);
  const [wellsReportData, setWellsReportData] = useState(null);
  const [wellsReportLoading, setWellsReportLoading] = useState(false);
  const [searchCoordinates, setSearchCoordinates] = useState(null);
  const [searchRadius, setSearchRadius] = useState(null);
  const [searchUnits, setSearchUnits] = useState(null);

  const handleWellsDialogClose = useCallback(
    () => setWellsReportDialogOpen(false),
    []
  );

  const [eventsRegistered, setEventsRegistered] = useState(false);
  const popUpRef = useRef(
    new mapboxgl.Popup({
      maxWidth: "400px",
      offset: 15,
      focusAfterOpen: false,
    })
  );
  const [popupVisible, _setPopupVisible] = useState(true);
  const popupVisibleRef = useRef(popupVisible);
  const setPopupVisible = (state) => {
    popupVisibleRef.current = state;
    _setPopupVisible(state);
  };

  const polygonRef = useRef(null);
  const radiusRef = useRef(null);
  const pointRef = useRef(null);
  const lineRef = useRef(null);
  const measurementsContainerRef = useRef(null);

  const { doToast } = useApp();

  const handleClearMeasurements = () => {
    draw.deleteAll();
    polygonRef.current.innerHTML = "";
    radiusRef.current.innerHTML = "";
    pointRef.current.innerHTML = "";
    lineRef.current.innerHTML = "";
    setMeasurementsVisible(false);
  };

  // Fetch a list of sources  and layers to add to the map
  const { sources } = useSources();
  const { layers, setLayers } = useLayers();

  const wells = sources.find((item) => item.id === "clearwater-wells")?.data
    ?.features;

  const {
    addBuffersToMap,
    searchRadiusBuffers,
    handleControlEnabled: handleEnableSearchRadiusControl,
    handleSearchRadiusBuffersChange: updateSearchRadiusBuffers,
    handleClearSearchRadiusBuffers,
    resetSearchRadiusBuffers,
    reportMode,
    setReportMode,
  } = useSearchRadius({ enabled: false });

  // Use refs to track report mode and buffers for the click handler
  const reportModeRef = useRef(reportMode);
  const searchRadiusBuffersRef = useRef(searchRadiusBuffers);

  // Update refs when state changes
  useEffect(() => {
    reportModeRef.current = reportMode;
  }, [reportMode]);

  useEffect(() => {
    searchRadiusBuffersRef.current = searchRadiusBuffers;
  }, [searchRadiusBuffers]);

  //adds control features as extended by MapboxDrawGeodesic (draw circle)
  let modes = MapboxDraw.modes;
  modes = MapboxDrawGeodesic.enable(modes);

  const [draw] = useState(
    new MapboxDraw({
      modes,
      controls: {
        polygon: true,
        point: true,
        trash: true,
        line_string: true,
      },
      displayControlsDefault: false,
      userProperties: true,
    })
  );

  useEffect(() => {
    measurementsVisible
      ? (measurementsContainerRef.current.style.display = "block")
      : (measurementsContainerRef.current.style.display = "none");
  }, [measurementsVisible]);

  /**
   * Function responsible for initializing the map
   * Once the map is loaded, store a reference to the map in
   * our application state and update the map status
   */
  const initializeMap = useCallback(() => {
    if (ref?.current && !map?.loaded()) {
      const mapInstance = new mapboxgl.Map({
        container: ref.current,
        ...mapConfig,
      });

      mapInstance?.on("load", () => {
        mapLogger.log("Map loaded");
        setMap(mapInstance);
      });
    }
    //MJB removed map from dependency array because it set an endless loop
  }, [ref, mapConfig]); //eslint-disable-line

  //MJB adding some logic to resize the map when the map container ref size changes
  //ResizeObserver watches for changes in bounding box for ref
  //debounce delays the resize function by 100ms
  useEffect(() => {
    if (map) {
      const resizer = new ResizeObserver(debounce(() => map.resize(), 100));
      resizer.observe(ref.current);
      return () => {
        resizer.disconnect();
      };
    }
  }, [map, ref]);

  /**
   * Function responsible for adding sources and layers to the map
   * There are a number of checks in place to ensure that sources
   * only get added to the map once the map and sources are loaded and
   * to ensure that layers are only added to the map once the layers are
   * loaded and the associated sources are added to the map
   */
  const loadMapData = useCallback(() => {
    const shouldAddData =
      !!map &&
      map?.loaded() &&
      sources?.length > 0 &&
      layers?.length > 0 &&
      !dataAdded;

    if (shouldAddData) {
      sources.forEach((source) => {
        const { id, ...rest } = source;
        const sourceExists = !!map.getSource(id);
        if (!sourceExists) {
          map.addSource(id, rest);
        }
      });

      mapLogger.log("Sources added to map");

      layers
        .sort((a, b) => ((a.drawOrder || 0) > (b.drawOrder || 0) ? -1 : 1))
        .forEach((layer) => {
          const { lreProperties, ...rest } = layer;
          const layerExists = map.getLayer(layer.id);
          if (!layerExists) {
            if (
              (layer.id.includes("eagleview-") ||
                layer.id.includes("open-street-map")) &&
              map.getLayer("gl-draw-polygon-fill-inactive.cold")
            ) {
              map.addLayer(rest, "gl-draw-polygon-fill-inactive.cold");
            } else map.addLayer(rest);
          }
        });

      mapLogger.log("Layers added to map");

      setDataAdded(true);
    }
  }, [dataAdded, layers, map, sources]);

  const addMapControls = useCallback(() => {
    const shouldAddControls = !!map;
    if (shouldAddControls) {
      //top left controls
      map.addControl(new mapboxgl.NavigationControl(), "top-left");
      map.addControl(new mapboxgl.FullscreenControl(), "top-left");
      map.addControl(
        new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true,
          },
          // When active the map will receive updates to the device's location as it changes.
          trackUserLocation: true,
          // Draw an arrow next to the location dot to indicate which direction the device is heading.
          showUserHeading: true,
        }),
        "top-left"
      );
      map.addControl(new ResetZoomControl(), "top-left");

      //top right controls
      map.addControl(
        new MapboxGeocoder({
          accessToken: mapboxgl.accessToken,
          localGeocoder: coordinatesGeocoder,
          zoom: 16,
          mapboxgl: mapboxgl,
          reverseGeocode: true,
          placeholder: "Address/Coords Search",
        }),
        "top-right"
      );

      //bottom left controls
      map.addControl(
        new mapboxgl.ScaleControl({ unit: "imperial", maxWidth: 250 }),
        "bottom-left"
      );
      map.addControl(
        new RulerControl({
          units: "feet",
          labelFormat: (n) => `${n.toFixed(2)} ft`,
        }),
        "bottom-left"
      );

      map.on("draw.modechange", () => {
        if (draw.getMode() !== "simple_select") {
          setPopupVisible(false);
        } else setPopupVisible(true);
        // setPopupVisible(!popupVisibleRef.current);
      });

      //event listener to run function updateArea during each draw action to handle measurements popup
      const drawActions = ["draw.create", "draw.update", "draw.delete"];
      drawActions.forEach((item) => {
        map.on(item, (event) => {
          const geojson = event.features[0];
          const type = event.type;
          updateArea(
            geojson,
            type,
            polygonRef,
            radiusRef,
            pointRef,
            lineRef,
            measurementsContainerRef,
            draw,
            setMeasurementsVisible
          );
        });
      });

      //bottom right controls
      //draw controls do not work correctly on touch screens
      !isTouchScreenDevice() &&
        map.addControl(draw, "bottom-right") &&
        !isTouchScreenDevice() &&
        map.addControl(new DragCircleControl(draw), "bottom-right");
    }
  }, [map]); // eslint-disable-line

  const addMapEvents = useCallback(() => {
    if (process.env.NODE_ENV === "development") {
      map?.on("zoom", () => {
        setZoomLevel(map?.getZoom());
      });
    }

    const shouldAddClickEvent = map && layers?.length > 0 && dataAdded;
    if (shouldAddClickEvent && !eventsRegistered) {
      //MJB add event listener for all circle and symbol layers
      // pointer on mouseover
      const cursorPointerLayerIds = layers
        .filter(
          (layer) =>
            ["circle", "symbol"].includes(layer.type) &&
            !layer.lreProperties?.popup?.excludePopup
        )
        .map((layer) => layer.id);
      cursorPointerLayerIds.forEach((layerId) => {
        map.on("mouseenter", layerId, () => {
          map.getCanvas().style.cursor = "pointer";

          map.on("mouseleave", layerId, () => {
            map.getCanvas().style.cursor = "";
          });
        });
      });

      map.on("click", "bell-parcels-fill", (e) => {
        if (e.features.length === 0) return;

        let clickedID = e.features[0].id;
        let clickedCurrentState = e.features[0].state.clickToHighlightParcels;
        //adds hover-state border
        map.setFeatureState(
          {
            source: "bell-parcels",
            id: clickedID,
            sourceLayer: "parcels",
          },
          {
            clickToHighlightParcels: !clickedCurrentState,
          }
        );
      });
      map.on("click", "bell-subdivisions-fill", (e) => {
        if (e.features.length === 0) return;

        let clickedID = e.features[0].id;
        let clickedCurrentState =
          e.features[0].state.clickToHighlightBellSubdivisions;

        //adds hover-state border
        map.setFeatureState(
          {
            source: "bell-subdivisions",
            id: clickedID,
            sourceLayer: "Bell_CAD_Subdivisions-3vq1qd",
          },
          {
            clickToHighlightBellSubdivisions: !clickedCurrentState,
          }
        );
      });
      map.on("click", "public-water-system-boundaries-fill", (e) => {
        if (e.features.length === 0) return;

        let clickedID = e.features[0].id;
        let clickedCurrentState =
          e.features[0].state.clickToHighlightPublicWaterSystemBoundaries;

        //adds hover-state border
        map.setFeatureState(
          {
            source: "public-water-system-boundaries",
            id: clickedID,
            sourceLayer: "PublicWaterSystem_Boundaries-85nriy",
          },
          {
            clickToHighlightPublicWaterSystemBoundaries: !clickedCurrentState,
          }
        );
      });

      map.on("click", async (e) => {
        setVirtualBoreCoordinates({
          lat: e.lngLat.lat,
          lon: e.lngLat.lng,
        });

        const features = map.queryRenderedFeatures(e.point);

        const clickCoordinates = [e.lngLat.lng, e.lngLat.lat];

        addBuffersToMap({
          map: map,
          coordinates: clickCoordinates,
        });

        // If in report mode, send the request to generate well report
        if (
          reportModeRef.current &&
          searchRadiusBuffersRef.current.buffer1.bufferAmount > 0
        ) {
          try {
            const payload = {
              coordinates: clickCoordinates,
              radius: searchRadiusBuffersRef.current.buffer1.bufferAmount,
              units: searchRadiusBuffersRef.current.buffer1.units,
            };

            // Client-side radius validation to prevent poor UX
            const radiusLimit =
              REPORT_LIMITS.radius.hard[payload.units] ||
              REPORT_LIMITS.radius.hard.meters;
            const radiusSoftLimit =
              REPORT_LIMITS.radius.soft[payload.units] ||
              REPORT_LIMITS.radius.soft.meters;

            if (parseFloat(payload.radius) > radiusLimit) {
              doToast(
                "error",
                `Search radius exceeds maximum allowed (${radiusLimit} ${payload.units}). Please reduce your search area.`
              );
              return; // Don't open dialog or make API call
            }

            // Show soft limit warning
            if (parseFloat(payload.radius) > radiusSoftLimit) {
              doToast(
                "warning",
                `⚠️ Large search radius (${payload.radius} ${payload.units}) may take longer to process.`
              );
            }

            // Show immediate loading feedback
            setWellsReportLoading(true);
            setSearchCoordinates(clickCoordinates);
            setSearchRadius(payload.radius);
            setSearchUnits(payload.units);
            setWellsReportDialogOpen(true);

            // Get the access token for authentication
            const accessToken = await getAccessTokenSilently();

            const response = await axios.post(
              `${process.env.REACT_APP_ENDPOINT}/api/public-map/wells/report`,
              payload,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

            // Check for warnings and display them
            if (response.data.warnings && response.data.warnings.length > 0) {
              response.data.warnings.forEach((warning) => {
                doToast("warning", `⚠️ ${warning.message}`);
              });
            }

            // Store the response data and hide loading
            setWellsReportData(response.data.data);
            setWellsReportLoading(false);
          } catch (error) {
            setWellsReportLoading(false);

            // Handle limit exceeded errors (wells/parcels only - radius handled client-side)
            if (error.response?.data?.error === "LIMIT_EXCEEDED") {
              const limitData = error.response.data.limit;
              let errorMessage = error.response.data.message;

              // Provide specific guidance based on limit type
              if (limitData?.type === "wells") {
                errorMessage += " Consider using a smaller search radius.";
              } else if (limitData?.type === "parcels") {
                errorMessage +=
                  " This area has too many parcels. Try a smaller search area.";
              }

              doToast("error", errorMessage);
              setWellsReportDialogOpen(false);
            } else {
              // Generic error handling
              doToast(
                "error",
                "Error generating wells report. Please try again."
              );
            }
          }
        }

        //Ensure that if the map is zoomed out such that multiple
        //copies of the feature are visible, the popup appears
        //over the copy being pointed to.
        //only for features with the properties.lat/long field (clearwater wells)
        const coordinates = features[0]?.properties?.latitude_dd
          ? [
              features[0].properties.longitude_dd,
              features[0].properties.latitude_dd,
            ]
          : [e.lngLat.lng, e.lngLat.lat];

        //MJB add check for popups so they only appear on our dynamic layers
        //also remove any layer that has the property:
        //lreProperties.popup.blockPopup
        const popupLayerIds = layers
          .filter((layer) => !layer?.lreProperties?.popup?.excludePopup)
          .map((layer) => layer.id);

        const myFeatures = features.filter((feature) =>
          popupLayerIds.includes(feature?.layer?.id)
        );

        if (myFeatures.length > 0) {
          // create popup node
          const popupNode = document.createElement("div");
          ReactDOM.render(
            //MJB adding style providers to the popup
            <StylesProvider jss={jss}>
              <MuiThemeProvider theme={createTheme(theme.currentTheme)}>
                <ThemeProvider theme={createTheme(theme.currentTheme)}>
                  <Popup
                    currentUser={currentUser}
                    layers={layers}
                    setDataVizVisible={setDataVizVisible}
                    setDataVizWellNumber={setDataVizWellNumber}
                    setDataVizGraphType={setDataVizGraphType}
                    features={myFeatures}
                  />
                </ThemeProvider>
              </MuiThemeProvider>
            </StylesProvider>,
            popupNode
          );
          popUpRef.current
            .setLngLat(coordinates)
            .setDOMContent(popupNode)
            .addTo(map);
        }
      });

      // //handles copying coordinates and measurements to the clipboard
      const copyableRefs = [polygonRef, radiusRef, pointRef, lineRef];
      copyableRefs.forEach((ref) => {
        ref.current.addEventListener("click", (e) => {
          handleCopyCoords(e.target.textContent);
        });
      });

      setEventsRegistered(true);
      mapLogger.log("Event handlers attached to map");
    }
  }, [
    map,
    layers,
    dataAdded,
    eventsRegistered,
    addBuffersToMap,
    doToast,
    theme.currentTheme,
    currentUser,
    getAccessTokenSilently,
  ]);

  /**
   * Handler used to apply user's filter values to the map instance
   * We rely on the `setFilter` method available on the map instance
   * and Mapbox expressions to apply filters to the wells layer
   * This function translates the filter values into valid
   * Mapbox expressions
   * Mapbox expressions are gnarly, powerful black box.
   * See https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/
   * The easiest way to get a feel for expressions is to create a new map
   * in Mapbox Studio, add a map layer, apply some filters to the layer and
   * then click on the code icon in the sidebar drawer to inspect the
   * Mapbox expression that is generated for the applied filters
   * (ask Ben Tyler if this doesn't make sense)
   * @param {object} filterValues object representing all of the current
   * filter values
   */
  const updateLayerFilters = useCallback(
    (filterValues) => {
      if (!!map) {
        /**
         * Setting to all means that all conditions must be met
         * Equivalent to and in SQL, change to "any" for the or
         * equivalent
         */
        const mapFilterExpression = ["all"];
        Object.values(filterValues).forEach((filter) => {
          if (filter.type === "multi-select") {
            mapFilterExpression.push([
              "match",
              ["get", filter.layerFieldName],
              [...filter.value].length ? [...filter.value] : "",
              true,
              false,
            ]);
          } else if (filter.type === "boolean") {
            //MJB only apply filter if toggle is true
            //MJB no filter applied if toggle is false
            if (filter.value) {
              mapFilterExpression.push([
                "==",
                ["get", filter.layerFieldName],
                filter.value,
              ]);
            }
          }
        });
        map.setFilter("clearwater-wells-circle", mapFilterExpression);
        map.setFilter("clearwater-wells-symbol", mapFilterExpression);
        mapLogger.log(
          "Filters updated on the clearwater-wells-circle/symbol layer"
        );
      }
    },
    [map]
  );

  /**
   * Handler used to update paint styles applied to map layer
   * This is used to support the color wells by x control
   * We look through each provided paint style property and apply the
   * style rule to the layer
   * Reference https://docs.mapbox.com/mapbox-gl-js/api/map/#map#setpaintproperty
   * @param {object} layer object representing a map layer
   */
  const updateLayerStyles = (layer) => {
    if (!!map) {
      Object.entries(layer.paint).forEach(([ruleName, ruleValue]) => {
        map.setPaintProperty(layer.layerId, ruleName, ruleValue);
      });
      setLayers((prevState) => {
        return prevState.map((d) => {
          if (d.id !== layer.layerId) return d;
          return {
            ...d,
            paint: {
              ...d.paint,
              ...layer.paint,
            },
          };
        });
      });
      mapLogger.log(
        "Paint styles updated on the clearwater-wells-circle layer"
      );
    }
  };

  /**
   * Handler used to update the visibility property on a layer
   * We employ special logic in this handler to allow for toggling
   * the visibility of grouped layers on and off
   * This allows us to display a single item in the layers list
   * but to control the visibility of multiple map layers at once
   * A common use case would be for something like parcels where
   * you want to display a layer for the parcel outlines and a layer
   * for the parcel fill.
   * This approach allows us to only show one layer in
   * the layer control list but to turn both layers on/off
   * @param {string | number} options.id ID associated with the layer or layer group
   * @param {boolean} options.boolean whether the layer is on/off
   */
  const updateLayerVisibility = useCallback(
    ({ id, visible }) => {
      /**
       * Get a list of the IDs for the layers that need to have their
       * visibility updated
       * The ID that is passed to the handler will either be the ID for
       * the layer or an ID for a layer group
       */
      const groupedLayerIds = layers.find(
        //if the id is a label group, then we should only toggle the label and not the whole layer group
        (layer) => layer.id === id && layer?.lreProperties?.labelGroup
      )
        ? [id]
        : layers
            ?.filter((layer) => {
              const key = layer?.lreProperties?.layerGroup || layer.id;
              return key === id;
            })
            .map(({ id }) => id);

      if (!!map) {
        /**
         * Loop through all of the layers and update the visibility
         * for all of the layers associated with the layer toggled
         * in the layer control
         */
        const updatedLayers = layers.map((layer) => {
          if (!!map.getLayer(layer.id) && groupedLayerIds.includes(layer.id)) {
            const visibleValue = visible ? "visible" : "none";
            map.setLayoutProperty(layer.id, "visibility", visibleValue);
            return {
              ...layer,
              layout: {
                ...layer?.layout,
                visibility: visibleValue,
              },
            };
          }
          return layer;
        });
        setLayers(updatedLayers);
        mapLogger.log(
          `Visibility set to ${
            visible ? "visible" : "none"
          } for the ${id} layer`
        );
      }
    },
    [layers, map, setLayers]
  );

  const updateLayerOpacity = useCallback(
    ({ id, opacity }) => {
      if (!!map) {
        const updatedLayers = layers.map((layer) => {
          if (!!map.getLayer(id) && layer.id === id) {
            map.setPaintProperty(id, "fill-opacity", opacity);
            return {
              ...layer,
              paint: {
                ...layer?.paint,
                "fill-opacity": opacity,
              },
            };
          }
          return layer;
        });
        setLayers(updatedLayers);
        mapLogger.log(`Opacity set to ${opacity} for the ${id} layer`);
      }
    },
    [layers, map, setLayers]
  );

  const updateLayerBooleanFilter = useCallback(
    ({ filterField, value }) => {
      const groupedLayerIds = layers
        .filter(
          //if the id is a label group, then we should only toggle the label and not the whole layer group
          (layer) => {
            return (
              layer?.lreProperties?.booleanToggle &&
              layer?.lreProperties?.booleanToggle[0] === filterField
            );
          }
        )
        .map((item) => item.id);

      if (!!map) {
        if (value === "true") {
          groupedLayerIds.map((layer) =>
            map.setFilter(layer, ["==", ["get", filterField], true])
          );
        }
        if (value === "false") {
          groupedLayerIds.map((layer) =>
            map.setFilter(layer, ["==", ["get", filterField], false])
          );
        }
        if (value === "all") {
          groupedLayerIds.map((layer) => map.setFilter(layer, null));
        }
      }
    },
    [layers, map]
  );

  const updateBasemap = useCallback(
    (style, filters) => {
      map?.setStyle(style.url);

      /**
       * After the map style changes we need to poll it every
       * 100 ms to determine if the new map style has loaded
       * After it is loaded, we add any existing sources and layers
       * back to the map
       */
      const checkStyleLoaded = setInterval(() => {
        const styleLoaded = map?.isStyleLoaded();
        if (styleLoaded) {
          clearInterval(checkStyleLoaded);
          setActiveBasemap(style.style);
          setDataAdded(false);
          loadMapData();

          //after the data is loaded into the map, apply all of the current user selected filters
          const checkDataAdded = setInterval(() => {
            if (dataAdded) {
              clearInterval(checkDataAdded);
              updateLayerFilters(filters);
            }
          }, 0);
        }
      }, 1000);
    },
    [dataAdded, loadMapData, map, updateLayerFilters]
  );

  // initialize and render the map
  useEffect(() => {
    initializeMap();
  }, [initializeMap]);

  // add all the sources and layers to the map
  useEffect(() => {
    loadMapData();
  }, [loadMapData]);

  // wire up all map related events
  useEffect(() => {
    addMapEvents();
  }, [addMapEvents]);

  // add all map controls
  useEffect(() => {
    addMapControls();
  }, [addMapControls]);

  return {
    activeBasemap,
    basemaps: BASEMAP_STYLES,
    layers,
    map,
    sources,
    zoomLevel,
    searchRadiusBuffers,
    handleClearSearchRadiusBuffers,
    handleEnableSearchRadiusControl,
    resetSearchRadiusBuffers,
    updateSearchRadiusBuffers,
    updateBasemap,
    updateLayerFilters,
    updateLayerStyles,
    updateLayerVisibility,
    updateLayerOpacity,
    updateLayerBooleanFilter,
    measurementsVisible,
    setMeasurementsVisible,
    polygonRef,
    radiusRef,
    pointRef,
    lineRef,
    measurementsContainerRef,
    handleClearMeasurements,
    dataVizVisible,
    setDataVizVisible,
    dataVizWellNumber,
    setDataVizWellNumber,
    dataVizGraphType,
    setDataVizGraphType,
    eventsRegistered,
    virtualBoreCoordinates,
    setVirtualBoreCoordinates,
    virtualBoreVisible,
    setVirtualBoreVisible,
    wells,
    popupVisibleRef,
    popupVisible,
    setPopupVisible,
    reportMode,
    setReportMode,
    wellsReportDialogOpen,
    handleWellsDialogClose,
    wellsReportData,
    wellsReportLoading,
    searchCoordinates,
    searchRadius,
    searchUnits,
  };
};

export { useMap };
